/**
 * Presentational primitives for the multi-candidate evaluation-set surface.
 *
 * Client-facing (CHRO/recruiter), so brand-styled with the local `C/DM/UI`
 * tokens (warm paper, ink neutrals, red accent) — the same system the result
 * screens and the calibration data hub use — and plain-HR copy per the tone
 * contract.
 */

import type {
  EvaluationSetReviewStatus,
  EvaluationSetStatus,
} from "@shared/labs/level-assessment-evaluation-set";
import type { CSSProperties, ReactNode } from "react";
import { C, UI } from "../results/ui-tokens";

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}

/** Two-letter initials from a name, for the candidate avatar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** A white content panel. */
export function Card({
  children,
  style,
  className,
}: {
  children: ReactNode;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div
      className={className}
      style={{
        background: C.white,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        padding: 20,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Empty / hint block. */
export function Muted({ children }: { children: ReactNode }) {
  return <div style={{ ...UI, fontSize: 13, color: C.ink50, lineHeight: 1.55 }}>{children}</div>;
}

/** Status of a whole set (the batch lifecycle), as a colored chip. */
export function statusChipProps(status: EvaluationSetStatus): {
  label: string;
  fg: string;
  bg: string;
} {
  switch (status) {
    case "evaluating":
      return { label: "Avaliando", fg: C.amberFg, bg: C.amberBg };
    case "ready":
      return { label: "Pronto", fg: C.greenFg, bg: C.greenBg };
    default:
      return { label: "Arquivado", fg: C.ink50, bg: C.ink5 };
  }
}

/** Per-candidate review-progress, as label + tone. */
export function reviewChipProps(status: EvaluationSetReviewStatus): {
  label: string;
  fg: string;
  bg: string;
} {
  switch (status) {
    case "complete":
      return { label: "Revisado", fg: C.greenFg, bg: C.greenBg };
    case "partial":
      return { label: "Em revisão", fg: C.amberFg, bg: C.amberBg };
    default:
      return { label: "A revisar", fg: C.ink50, bg: C.ink5 };
  }
}
